import { HttpContextToken, HttpErrorResponse } from '@angular/common/http';

/**
 * One error shape for the whole app.
 *
 * Before this, every component invented its own reading of a failure. The same
 * upstream 500 surfaced as "Something went wrong", as a raw `[object Object]`,
 * and — in three places — as nothing at all, because the error callback was
 * omitted and the observable failed silently into a stranded spinner.
 *
 * The BFF is not consistent either: it returns `{error}` from some handlers,
 * `{message}` from others, and `{success:false, message}` from the auth
 * middleware. `messageFrom` below is the single place that knows all three.
 */
export interface ApiError {
  /** HTTP status, or 0 for a network/CORS failure where the browser tells us nothing. */
  status: number;
  /** Human-readable, safe to display. Never a raw payload dump. */
  message: string;
  /** True when the request never reached the server. */
  offline: boolean;
  /** Correlation id sent on the request, echoed in BFF logs. */
  requestId: string;
  /** The original, for callers that need to branch on specifics. */
  original: HttpErrorResponse;
}

/**
 * Suppresses the automatic error toast for one request.
 *
 * Polls are the reason this exists. A plan build issues ~140 requests, most of
 * them polls, and a single blip on any of them would raise a toast the user can
 * do nothing about — while the poller itself is already retrying. Toasting is
 * for actions the user took and expects an answer to.
 */
export const SILENCE_ERRORS = new HttpContextToken<boolean>(() => false);

/**
 * Opts a request into retry.
 *
 * Off by default, deliberately. Retrying is only safe when the request is
 * idempotent, and the app's POSTs are not: `POST /chat` enqueues an agent job
 * and `POST /avails/quote` bills an upstream quota. A blind retry-on-5xx would
 * double-charge both. GETs opt in; anything that mutates or costs money does not.
 */
export const RETRY_ON_FAILURE = new HttpContextToken<boolean>(() => false);

/** Reads a displayable message out of any of the shapes the BFF actually returns. */
export function messageFrom(error: HttpErrorResponse): string {
  if (error.status === 0) {
    return 'Cannot reach the server. Check your connection and try again.';
  }

  const body = error.error;
  if (typeof body === 'string' && body && !body.startsWith('<')) return body;
  if (body && typeof body === 'object') {
    const candidate = body.message ?? body.error ?? body.detail;
    if (typeof candidate === 'string' && candidate) return candidate;
  }

  // Status-specific text beats "Http failure response for <url>: 500 Internal
  // Server Error", which names the internal host and helps the user with nothing.
  switch (error.status) {
    case 401: return 'Your session has expired. Please sign in again.';
    case 403: return "You don't have access to this.";
    case 404: return 'Not found.';
    case 413: return 'That plan is too large to save.';
    case 429: return 'Too many requests — please wait a moment.';
    case 502:
    case 503:
    case 504: return 'The service is temporarily unavailable. Please try again.';
    default:  return 'Something went wrong. Please try again.';
  }
}

export function toApiError(error: HttpErrorResponse, requestId: string): ApiError {
  return {
    status: error.status,
    message: messageFrom(error),
    offline: error.status === 0,
    requestId,
    original: error,
  };
}

/**
 * The BFF's stable `code` on a refused request's body (`{ success: false, code, message }` —
 * e.g. the tactic-state 409's `tactic_state_wipe_refused` / `tactic_state_signals_wipe_refused`),
 * or '' when it carries none. Reads the raw `HttpErrorResponse` AND the `ApiError` the error
 * interceptor rethrows in its place — the app's callers only ever see the latter, whose body is on
 * `original.error`: read from `error` alone, the code was always '' in the app (QA E2E B23, the
 * MPL-2026-090 toast).
 */
export function bffErrorCode(err: unknown): string {
  const response = (err as Partial<ApiError> | null | undefined)?.original ?? err;
  const code = (response as { error?: { code?: unknown } } | null | undefined)?.error?.code;
  return typeof code === 'string' ? code : '';
}

/** The backend's stable code for "this platform has no implementation of that step yet". */
export const PLATFORM_CAPABILITY_UNAVAILABLE = 'platform_capability_unavailable';

/**
 * True for the backend's 409 `platform_capability_unavailable` (TikTok plans spec
 * §4.4), whether the caller holds the raw `HttpErrorResponse` or the `ApiError` the
 * error interceptor rethrows. The UI treats it exactly like a capability that is
 * off — a Coming-soon state, never an error toast.
 */
export function isCapabilityUnavailable(err: unknown): boolean {
  return capabilityUnavailableResponse(err) !== null;
}

/**
 * The capability the backend's 409 names (`'signals'`, `'forecast'`, …), or null
 * when `err` is not that 409 (or names none).
 */
export function unavailableCapability(err: unknown): string | null {
  const capability = capabilityUnavailableResponse(err)?.error?.error?.capability;
  return typeof capability === 'string' && capability ? capability : null;
}

function capabilityUnavailableResponse(err: unknown): HttpErrorResponse | null {
  const response = err instanceof HttpErrorResponse
    ? err
    : (err as Partial<ApiError> | null | undefined)?.original;
  return response instanceof HttpErrorResponse
    && response.status === 409
    && response.error?.error?.code === PLATFORM_CAPABILITY_UNAVAILABLE
    ? response
    : null;
}
